export interface Client {
  id: string;
  name: string;
  age: number;
  gender: string;
  property: string;
  bigFivePersonality: {
    openness: number;
    conscientiousness: number;
    extraversion: number;
    agreeableness: number;
    neuroticism: number;
  };
}

export interface Agent {
  id: string;
  name: string;
  role: string;
}

export interface Message {
  id: string;
  senderId: string;
  senderType: 'client' | 'agent';
  content: string;
  timestamp: Date;
  sentiment: number; // -3 to +3
  emotions: string[];
  emotionsCount: { [emotion: string]: number }; // 0-1 intensity scores
}

export interface Conversation {
  id: string;
  clientId: string;
  agentIds: string[];
  status: 'In Progress' | 'Solved';
  sentiment: string;
  emotions: string[];
  topics: string[];
  summary: string;
  messages: Message[];
  lastUpdated: Date;
  startedAt: Date;
}

export const sentimentLevels = [
  'strong negative',
  'moderate negative', 
  'weak negative',
  'neutral',
  'weak positive',
  'moderate positive',
  'strong positive'
];

export const emotionTypes = [
  'anger', 'surprise', 'disgust', 'joy', 'fear', 'sadness', 'neutral'
];

export const topicTypes = [
  'Room & Space Issues', 'Housemate Relations', 'Bills & Expenses', 'House Rules & Policies', 'Maintenance & Repairs', 'Shared Facilities', 'Noise & Disturbances', 'Guest & Visitor Policies', 'Safety & Security', 'Move-in/Move-out', 'Social Activities', 'General Living Concerns'
];
//deletion start

//deletion end
export function getSentimentScore(sentiment: string): number {
  const index = sentimentLevels.indexOf(sentiment);
  return index - 3; // Convert to -3 to +3 scale
}

export function getSentimentColor(sentiment: string): string {
  const score = getSentimentScore(sentiment);
  if (score <= -2) return 'bg-red-500';
  if (score === -1) return 'bg-orange-500'; 
  if (score === 0) return 'bg-gray-500';
  if (score === 1) return 'bg-yellow-500';
  if (score >= 2) return 'bg-green-500';
  return 'bg-gray-500';
}

export function getEmotionColor(emotion: string): string {
  switch (emotion) {
    case 'anger': return '#ef4444';
    case 'fear': return '#f97316';
    case 'disgust': return '#84cc16';
    case 'sadness': return '#3b82f6';
    case 'surprise': return '#8b5cf6';
    case 'joy': return '#10b981';
    case 'neutral': return '#6b7280';
    default: return '#6b7280';
  }
}

export function getSentimentAbbreviation(sentimentScore: number): string {
  if (sentimentScore === 9) return 'Null'; //edited
  if (sentimentScore <= -3) return 'SN'; // Strong Negative
  if (sentimentScore === -2) return 'MN'; // Moderate Negative
  if (sentimentScore === -1) return 'WN'; // Weak Negative
  if (sentimentScore === 0) return 'N';   // Neutral
  if (sentimentScore === 1) return 'WP';  // Weak Positive
  if (sentimentScore === 2) return 'MP';  // Moderate Positive
  if (sentimentScore >= 3) return 'SP';   // Strong Positive
  return 'Default'; //edited
}

export function getConversationTrend(conversation: Conversation): string {
  const messages = conversation.messages.filter(m => m.senderType === 'client');
  if (messages.length === 0) return 'N - N';

  //edition start
  let firstMessageIndex = 0;
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].sentiment !== 9) {
      firstMessageIndex = i;
      break;
    }
  }
  const firstMessage = messages[firstMessageIndex];

  let lastMessageIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].sentiment !== 9) {
      lastMessageIndex = i;
      break;
    }
  }
  if (lastMessageIndex === -1){
    lastMessageIndex = messages.length - 1
  }
  const lastMessage = messages[lastMessageIndex];
  
  const firstSentiment = getSentimentAbbreviation(firstMessage.sentiment);
  const lastSentiment = getSentimentAbbreviation(lastMessage.sentiment);
  
  if (firstSentiment === "Null" && lastSentiment === "Null") {
    return "N/A";
  }
  //edition end
  return `${firstSentiment} - ${lastSentiment}`;
}

export function getTrendColor(conversation: Conversation): string {
  const messages = conversation.messages.filter(m => m.senderType === 'client');
  if (messages.length === 0) return 'bg-gray-50 text-gray-700 border-gray-300';
  
  //edition start
  let firstMessageIndex = 0;
  for (let i = 0; i < messages.length; i++) {
    if (messages[i].sentiment !== 9) {
      firstMessageIndex = i;
      break;
    }
  }
  const firstMessage = messages[firstMessageIndex];

  let lastMessageIndex = -1;
  for (let i = messages.length - 1; i >= 0; i--) {
    if (messages[i].sentiment !== 9) {
      lastMessageIndex = i;
      break;
    }
  }
  if (lastMessageIndex === -1){
    lastMessageIndex = messages.length - 1
  }
  const lastMessage = messages[lastMessageIndex];
  //edition end

  const firstSentiment = firstMessage.sentiment;
  const lastSentiment = lastMessage.sentiment;
  
  // Determine trend direction
  if (lastSentiment > firstSentiment) {
    // Improving sentiment
    return 'bg-green-50 text-green-700 border-green-300';
  } else if (lastSentiment < firstSentiment) {
    // Declining sentiment
    return 'bg-red-50 text-red-700 border-red-300';
  } else {
    // No change
    return 'bg-gray-50 text-gray-700 border-gray-300';
  }
}